import React, { useState, useEffect, useCallback, useRef } from 'react';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import ConversationLayers from '@components/CustomFields/ConversationLayers';
import ConversationOwner from '@components/CustomFields/ConversationOwner';
import * as apiHelper from '@components/Globals/Utils.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ApiConfig {
  url: string;
  token: string;
}

interface ConversationLayer {
  LayerId: number;
  isForAdd?: boolean;
}

interface ConversationGroup {
  GroupId: number;
  IsDefault?: boolean;
}

interface Conversation {
  ConfigValueId?: number;
  ConfigValueID?: number;
  selectedConfigValueId?: number;
  ConversationLayers: ConversationLayer[];
  ConversationGroups?: ConversationGroup[];
  ProjectId?: string;
  ProjectID?: string;
  layers?: ConversationLayer[];
  OwnedBy?: string;
  OwnedById?: string;
  dashboardGroupId?: string;
  layerCustomFields?: unknown;
  isAddLayerToCard?: boolean;
}

interface HeaderDetailsConfig {
  url: string;
  authToken: string;
  addLayerToCard?: (
    e: React.MouseEvent,
    conversation: Conversation,
    layer: ConversationLayer,
    callback: (layerData: ConversationLayer) => void
  ) => void;
  onChangedProject?: (e: React.MouseEvent | undefined, params: Conversation) => void;
  onChangedSecurityGroups?: (e: React.MouseEvent | undefined, params: Conversation) => void;
  onChangedGroupLayers?: (e: React.MouseEvent | undefined, params: Conversation) => void;
}

interface HeaderDetailsProps {
  config: HeaderDetailsConfig;
  item: Conversation;
  configValueId?: number;
  disableAutoSave?: boolean;
  isNew?: boolean;
  isConversationDetailsView?: boolean;
  canEditOwner?: boolean;
  section?: string;
  sendDataToCallback?: (data: unknown) => void;
  onChangedLayers?: (params: Conversation, isFromInfoWindow?: boolean) => void;
  onChangedOwner?: (params: Conversation, isFromInfoWindow?: boolean) => void;
  onUpdateConversationGroups?: (conversation: Conversation) => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function resolveConfigValueId(item: Conversation, configValueId?: number): number | undefined
{
  return item?.ConfigValueId ?? item?.ConfigValueID ?? configValueId;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const HeaderDetails: React.FC<HeaderDetailsProps> = ({
  config,
  item,
  configValueId: configValueIdProp,
  disableAutoSave = false,
  isNew = false,
  isConversationDetailsView = false,
  canEditOwner,
  section,
  sendDataToCallback,
  onChangedLayers: onChangedLayersProp,
  onChangedOwner: onChangedOwnerProp,
  onUpdateConversationGroups,
}) =>
{
  const apiConfig: ApiConfig = { url: config.url, token: config.authToken };

  const [conversation, setConversation] = useState<Conversation>(item);
  const [isProject, setIsProject] = useState(
    () => resolveConfigValueId(item, configValueIdProp) === 9,
  );
  const [isPeople] = useState(() => item?.ConfigValueId === 15);

  const isProjectView = window.location.href.includes('ProjectDetails');
  const isConversationDetails = window.location.href.includes('ConversationDetails');
  const personId = CorgeeStorageHelper.personId();

  // Refs to child components for imperative calls
  const layersRef = useRef<React.ComponentRef<typeof ConversationLayers>>(null);
  const ownerRef = useRef<React.ComponentRef<typeof ConversationOwner>>(null);
  const groupLayersRef = useRef<{
    onSelectGroupLayers: (l: ConversationLayer[]) => void;
    onChangeParentLayers: (l: ConversationLayer[]) => void;
    onChangeGroupsLayers: (g: ConversationGroup[]) => void;
      } | null>(null);
  const securityGroupsRef = useRef<{ setDefaultGroup: (g: ConversationGroup) => void } | null>(
    null,
  );

  // ── Sync props → state ────────────────────────────────────────────────────
  useEffect(() =>
  {
    if (!conversation || !item) return;

    const prevGroups = conversation.ConversationGroups;
    const prevLayers = conversation.ConversationLayers;
    const isProjectConv = conversation.ConfigValueId === 9;

    const groupsOrLayersChanged =
      isProjectConv &&
      prevGroups != null &&
      item.ConversationGroups != null &&
      item.ConversationGroups.length > 0 &&
      (prevGroups.length !== item.ConversationGroups.length ||
        prevLayers.length !== item.ConversationLayers.length) &&
      (isProjectView || isConversationDetails);

    if (groupsOrLayersChanged)
    {
      setConversation(prev => ({
        ...prev,
        ConversationGroups: item.ConversationGroups,
        ConversationLayers: item.ConversationLayers,
      }));

      if (groupLayersRef.current && item.ConversationLayers)
      {
        groupLayersRef.current.onChangeParentLayers(item.ConversationLayers);
      }
    }

    const configValueChanged =
      conversation.ConfigValueId !== item.ConfigValueId ||
      conversation.selectedConfigValueId !== item.selectedConfigValueId;

    if (configValueChanged)
    {
      const newConfigValueId = item.selectedConfigValueId ?? item.ConfigValueId;
      setConversation(prev => ({
        ...prev,
        ConfigValueId: newConfigValueId,
        selectedConfigValueId: newConfigValueId,
      }));
      setIsProject(newConfigValueId === 9);
    }
    // Intentionally only runs when `item` reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // ── reloadLayers ──────────────────────────────────────────────────────────
  const reloadLayers = useCallback(
    (stopPropagate?: boolean) =>
    {
      let params = '?';
      if (conversation.selectedConfigValueId)
      {
        params += `configValueId=${conversation.selectedConfigValueId}`;
      }
      const projectId = conversation.ProjectID ?? conversation.ProjectId;
      if (projectId) params += `&projectId=${projectId}`;

      const userDefaultLayerId = CorgeeStorageHelper.layerId();

      apiHelper
        .get(apiConfig, `api/layer/GetLayersByConfigValueId${params}`)
        .then((layersResponse: ConversationLayer[]) =>
        {
          let selectedLayer = layersResponse.find(
            l =>
              l.LayerId === parseInt(userDefaultLayerId ?? '') &&
              !CorgeeStorageHelper.isLayerHidden(l.LayerId),
          );

          if (!selectedLayer)
          {
            const userLayers: ConversationLayer[] = CorgeeStorageHelper.getAllLayers();
            outer: for (const ul of userLayers)
            {
              for (const lr of layersResponse)
              {
                if (ul.LayerId === lr.LayerId && !CorgeeStorageHelper.isLayerHidden(lr.LayerId))
                {
                  selectedLayer = lr;
                  break outer;
                }
              }
            }
          }

          setConversation(prev =>
          {
            const updated: Conversation = {
              ...prev,
              layers: layersResponse,
              ConversationLayers: selectedLayer ? [selectedLayer] : [],
            };
            if (!stopPropagate && config.onChangedProject)
            {
              config.onChangedProject(undefined, updated);
            }
            return updated;
          });
        })
        .catch((err: Error) => console.warn('Api Error', err));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversation.selectedConfigValueId, conversation.ProjectID, conversation.ProjectId],
  );

  // ── onChangedItemType ─────────────────────────────────────────────────────
  const onChangedItemType = useCallback(
    (newItemType: { ConfigValueId: number; DashboardGroupId?: string }) =>
    {
      const newIsProject = newItemType.ConfigValueId === 9;
      setIsProject(newIsProject);

      if (!newIsProject) return;

      apiHelper
        .get(apiConfig, 'api/group/GetGroups')
        .then((groups: ConversationGroup[]) =>
        {
          apiHelper
            .get(apiConfig, `api/Person/PersonGroupProject?PersonId=${personId}`)
            .then((defaultGroupId: number) =>
            {
              if (groups.length === 0) return;

              const groupDefault =
                groups.find(g => g.GroupId === defaultGroupId) ??
                groups.find(g => g.IsDefault) ??
                null;

              if (!groupDefault) return;

              setConversation(prev =>
              {
                const updated = { ...prev, ConversationGroups: [groupDefault] };
                sendDataToCallback?.(updated.layerCustomFields);
                return updated;
              });

              securityGroupsRef.current?.setDefaultGroup(groupDefault);
            })
            .catch((err: Error) => console.warn('Api Error', err));
        })
        .catch((err: Error) => console.warn('Api Error', err));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personId, sendDataToCallback],
  );

  // ── componentDidMount logic ───────────────────────────────────────────────
  useEffect(() =>
  {
    if (
      isProject &&
      conversation.ConfigValueId === 9 &&
      (!conversation.ConversationGroups || conversation.ConversationGroups.length === 0)
    )
    {
      apiHelper
        .get(apiConfig, `api/group/GetGroupsByProject?projectId=${conversation.ProjectId}`)
        .then((data: ConversationGroup[]) =>
        {
          if (data.length > 0)
          {
            setConversation(prev =>
            {
              const updated = { ...prev, ConversationGroups: data };
              onUpdateConversationGroups?.(updated);
              return updated;
            });
          }
        })
        .catch((err: Error) => console.warn('Api Error', err));
    }

    if (isProject && isNew)
    {
      onChangedItemType({
        ConfigValueId: 9,
        DashboardGroupId: conversation.dashboardGroupId,
      });
    }

    if (conversation.ConfigValueId !== 9 && isNew)
    {
      reloadLayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── onUpdateLayersConversation ────────────────────────────────────────────
  const _onUpdateLayersConversation = useCallback((conversationLayers: ConversationLayer[]) =>
  {
    setConversation(prev => ({ ...prev, ConversationLayers: conversationLayers }));
    (layersRef.current as any)?.onSelectLayer(conversationLayers);
    (layersRef.current as any)?.onUpdateOriginalSelectedIds(conversationLayers);
    groupLayersRef.current?.onSelectGroupLayers(conversationLayers);
  }, []);
  void _onUpdateLayersConversation;

  // ── addLayerToCard ────────────────────────────────────────────────────────
  const addLayerToCard = useCallback(
    (e: React.MouseEvent, layer: ConversationLayer) =>
    {
      if (!config.addLayerToCard) return;

      if (!isConversationDetailsView)
      {
        const convCopy = {
          ...conversation,
          ConfigValueId: conversation.ConfigValueId ?? conversation.selectedConfigValueId,
        };

        config.addLayerToCard(e, convCopy, layer, layerData =>
        {
          if (isNew)
          {
            setConversation(prev => ({
              ...prev,
              ConversationLayers: [...prev.ConversationLayers.filter(l => !l.isForAdd), layerData],
              isAddLayerToCard: true,
            }));
            config.onChangedProject?.(undefined, conversation);
          }
        });
      }
      else
      {
        config.addLayerToCard(e, conversation, layer, () => {});
      }
    },
    [config, conversation, isConversationDetailsView, isNew],
  );

  // ── Event handlers ────────────────────────────────────────────────────────
  const _onChangedSecurityGroups = useCallback(
    (e: React.MouseEvent | undefined, params: Conversation) =>
    {
      if (isNew)
      {
        setConversation(prev => ({
          ...prev,
          ConversationGroups: params.ConversationGroups,
          ConversationLayers: params.ConversationLayers,
        }));
        if (e !== undefined) onUpdateConversationGroups?.({ ...conversation, ...params });
        groupLayersRef.current?.onChangeGroupsLayers(params.ConversationGroups ?? []);
      }
      else
      {
        config.onChangedSecurityGroups?.(e, params);
      }
    },
    [isNew, conversation, onUpdateConversationGroups, config],
  );
  void _onChangedSecurityGroups;

  const _onChangedGroupLayers = useCallback(
    (e: React.MouseEvent | undefined, params: Conversation) =>
    {
      config.onChangedGroupLayers?.(e, params);
    },
    [config],
  );
  void _onChangedGroupLayers;

  const _onChangedProject = useCallback(
    (e: React.MouseEvent, params: Conversation) =>
    {
      setConversation(params);
      if (isNew)
      {
        setTimeout(() => reloadLayers(true));
      }
      else
      {
        config.onChangedProject?.(e, params);
      }
    },
    [isNew, reloadLayers, config],
  );
  void _onChangedProject;

  const onChangedLayers = useCallback(
    (params: Conversation, isFromInfoWindow?: boolean) =>
    {
      setConversation(params);
      onChangedLayersProp?.(params, isFromInfoWindow);
    },
    [onChangedLayersProp],
  );

  const onChangedOwner = useCallback(
    (params: Conversation, isFromInfoWindow?: boolean) =>
    {
      setConversation(params);
      onChangedOwnerProp?.(params, isFromInfoWindow);
    },
    [onChangedOwnerProp],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="color-wrapper no-layer">
      <ul className="inner">
        {!isPeople && (
          <li className="field-A layer-editable-field-item mr-3 mb-2">
            <span className="title">Layers</span>
            {React.createElement(ConversationLayers as any, {
              ref: layersRef,
              apiConfig,
              conversation,
              isNew,
              disableAutoSave,
              onChangedLayers,
              addLayerToCard,
              section,
            })}
          </li>
        )}

        {!isProject && !isPeople && (
          <li className="field-A owner-editable-field-item mr-3 mb-2">
            <span className="title">Card Owner</span>
            {React.createElement(ConversationOwner as any, {
              ref: ownerRef,
              apiConfig,
              conversation,
              isNew,
              disableAutoSave,
              onChangedOwner,
              section,
              config,
              canEditOwner,
            })}
          </li>
        )}
      </ul>
    </div>
  );
};

export default HeaderDetails;
